import React, { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { OrganizationEvents } from "@app/shared/src/api/api.gen";
import {
    AccountIcon,
    Btn,
    CloseIcon,
    Combo,
    DownloadIcon,
    FilterIcon,
    GridIcon,
    HelpIcon,
    ListIcon,
    LogoutIcon,
    Menu,
    MenuHeader,
    MenuIcon,
    MenuItem,
    MoonIcon,
    OmnisightMark,
    PanelLeftIcon,
    PlusIcon,
    QrIcon,
    SearchIcon,
    Select,
    SettingsIcon,
    Sheet,
    Spinner,
    SunIcon,
    SunMoonIcon,
    TruckPanelIcon,
    UsersIcon,
    useDocumentTheme,
} from "@app/shared/src/components_v2";
import { useLoad } from "../../hooks/useLoad";
import { ALL_GROUPS, useFleet } from "../providers/FleetProvider";
import { useOrg } from "../providers/OrgProvider";
import { useSession } from "../providers/SessionProvider";
import { AddOrganizationDialog } from "../shared/AddOrganizationDialog";
import { ProfileDialog } from "../shared/ProfileDialog";
import { SearchPalette } from "../shared/SearchPalette";

/**
 * The Fleet Commander frame: a black navigation rail with live counts, a topbar carrying search,
 * org, theme and the account menu, and the content outlet.
 *
 * Desktop-first — this is the safety director at a desk — but it has a real phone layout too,
 * because directors get paged about trucks at night: the rail folds into a drawer behind a menu
 * button, the search field becomes a magnifier, and the organization and group pickers (which
 * only fit the topbar from lg up) open in a sheet from a funnel button. Nothing in the frame is
 * ever wider than the phone, so the page never scrolls sideways.
 */

type NavItem = { to: string; label: string; Icon: React.FC<{ size?: number; className?: string }>; count?: number };

const THEME_KEY = "tb.theme";
const COLLAPSE_KEY = "tb.navCollapsed";
const STALE_MS = 15 * 60 * 1000;
const BADGE_REFRESH_MS = 60_000;

const THEME_LABEL: Record<string, string> = { auto: "Follows the system", light: "Day", dark: "Night" };

export const ConsoleShell: React.FC = () => {
    const { user, isChecking, logout } = useSession();
    const { orgs, org, setOrgId, isLoading: orgsLoading, error: orgsError } = useOrg();
    const { devices, groups, group, setGroupId } = useFleet();
    const location = useLocation();

    // Same data-theme mechanism as the cab, separate storage: a director's laptop preference and a
    // driver's phone preference are different contexts even when it's the same person.
    const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) ?? "auto");
    useDocumentTheme(theme);
    useEffect(() => {
        localStorage.setItem(THEME_KEY, theme);
    }, [theme]);
    const cycleTheme = () => setTheme(theme === "dark" ? "light" : theme === "light" ? "auto" : "dark");

    const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
    useEffect(() => {
        localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    }, [collapsed]);

    const [searchOpen, setSearchOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [addOrgOpen, setAddOrgOpen] = useState(false);
    // The phone's two sheets: the navigation drawer and the organization/group scope.
    const [navOpen, setNavOpen] = useState(false);
    const [scopeOpen, setScopeOpen] = useState(false);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setSearchOpen(true);
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    // A tap on a drawer link navigates; the drawer must not still be covering the destination.
    useEffect(() => {
        setNavOpen(false);
    }, [location.pathname]);

    // The drawer is fixed-position and sits over the page; while it is open, Escape closes it.
    useEffect(() => {
        if (!navOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setNavOpen(false);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [navOpen]);

    // The Events badge: how many fired in the last 24 hours. One count query on a slow poll —
    // close enough for a nav hint, and cheap enough to always have. Org-wide by design: the count
    // endpoint takes one device or one facility, and a badge that silently under-reported the
    // fleet would be worse than one that plainly counts the whole org.
    const events24h = useLoad<number | undefined>(
        () =>
            org?.id
                ? OrganizationEvents.getCountByOrganization(org.id, undefined, undefined, undefined, undefined, undefined, new Date(Date.now() - 86_400_000))
                : Promise.resolve(undefined),
        [org?.id],
        { pollMs: BADGE_REFRESH_MS },
    );

    if (isChecking) {
        return (
            <div className="grid h-full place-items-center bg-[var(--app)] text-[var(--muted)]">
                <Spinner />
            </div>
        );
    }

    if (!user) {
        // Send them back where they were headed after login rather than dumping them on Overview.
        return <Navigate to="/login" state={{ from: location.pathname }} replace />;
    }

    // Signed in, but in no organization: every page here is scoped to one, so the welcome page's
    // create-organization form is the only useful screen. Only once the list has actually loaded —
    // an empty list mid-fetch is not "no organizations" — and not on a failed fetch, which the
    // welcome page would read the same way.
    if (!orgsLoading && !orgsError && orgs.length === 0) {
        return <Navigate to="/welcome" replace />;
    }

    const online = devices.filter((d) => Date.now() - new Date(d.lastHeartbeat ?? 0).getTime() <= STALE_MS).length;

    const NAV: NavItem[] = [
        { to: "/overview", label: "Overview", Icon: GridIcon },
        { to: "/events", label: "Events", Icon: ListIcon, count: events24h.data },
        { to: "/media", label: "DVR Requests", Icon: DownloadIcon },
        { to: "/devices", label: "Devices", Icon: TruckPanelIcon, count: devices.length > 0 ? online : undefined },
        { to: "/qr", label: "Driver QR Codes", Icon: QrIcon },
        { to: "/team", label: "Team", Icon: UsersIcon },
    ];
    const NAV_END: NavItem[] = [
        { to: "/help", label: "Help Center", Icon: HelpIcon },
        { to: "/settings", label: "Settings", Icon: SettingsIcon },
    ];

    // One renderer for the rail and the drawer: the drawer is the rail, expanded, on a phone.
    const navLink = ({ to, label, Icon, count }: NavItem, isCollapsed = collapsed) => (
        <NavLink
            key={to}
            to={to}
            title={isCollapsed ? label : undefined}
            className={({ isActive }) =>
                `group relative mb-[3px] flex items-center rounded-[11px] text-[14.5px] font-medium ${
                    isCollapsed ? "justify-center px-0 py-3" : "gap-3 px-3 py-2.5"
                } ${
                    isActive
                        ? "bg-[var(--surface-2)] font-semibold text-[var(--text)] shadow-[inset_0_0_0_1px_var(--line-2)]"
                        : "text-[var(--muted)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
                }`
            }
        >
            {({ isActive }) => (
                <>
                    <Icon size={isCollapsed ? 25 : 22} />
                    {!isCollapsed && label}
                    {!isCollapsed && count !== undefined && (
                        <span className={`ml-auto text-[12.5px] font-semibold tabular-nums ${isActive ? "text-[var(--accent)]" : "text-[var(--faint)]"}`}>
                            {count}
                        </span>
                    )}
                    {/* Collapsed: name the icon on hover, since the label is hidden. */}
                    {isCollapsed && (
                        <span className="pointer-events-none absolute left-[calc(100%+12px)] z-50 hidden whitespace-nowrap rounded-[10px] border border-[var(--line-2)] bg-[var(--surface-3)] px-3 py-2 text-[13.5px] font-semibold text-[var(--text)] shadow-[0_12px_30px_rgba(0,0,0,.45)] group-hover:block">
                            {label}
                        </span>
                    )}
                </>
            )}
        </NavLink>
    );

    const initials = (user.name ?? user.email ?? "?")
        .split(/[\s@.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase())
        .join("");

    // Whether there is any scope to choose at all. One org and no groups means the funnel button
    // would open a sheet with nothing in it.
    const hasScope = orgs.length > 1 || groups.length > 0;
    const scopeLabel = [org?.name, groups.length > 0 ? (group?.name ?? "All groups") : undefined].filter(Boolean).join(" · ");

    const iconButton =
        "grid h-[46px] w-[46px] flex-none place-items-center rounded-[13px] border border-[var(--line-2)] bg-[var(--surface)] text-[var(--muted)] hover:text-[var(--text)]";

    return (
        <div className="flex h-full bg-[var(--app)] text-[var(--text)]">
            {/* Navigation rail */}
            <aside
                className={`hidden flex-none flex-col border-r border-[var(--line)] bg-[var(--side)] py-5 md:flex ${
                    collapsed ? "w-[76px] px-1.5" : "w-[216px] px-3.5"
                }`}
            >
                <div className={`mb-6 flex items-center gap-2 ${collapsed ? "justify-center" : "px-1"}`}>
                    {collapsed ? (
                        <button type="button" onClick={() => setCollapsed(false)} title="Expand navigation" aria-label="Expand navigation">
                            <OmnisightMark size={30} />
                        </button>
                    ) : (
                        // The mark sits in a row with the product name alone, so it centres on
                        // that and not on the name-plus-byline block. The byline then hangs
                        // underneath, indented past the mark (26px) and its gap (8px) to stay
                        // flush with the name. Both text elements keep their own classes, so the
                        // wrap and the type are exactly as they were.
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <OmnisightMark size={26} />
                                <div className="text-[15px] font-bold leading-tight tracking-tight">Fleet Commander</div>
                                <button
                                    type="button"
                                    onClick={() => setCollapsed(true)}
                                    aria-label="Collapse navigation"
                                    title="Collapse navigation"
                                    className="ml-auto grid h-[30px] w-[30px] flex-none place-items-center rounded-lg text-[var(--faint)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
                                >
                                    <PanelLeftIcon size={18} />
                                </button>
                            </div>
                            <div className="pl-[34px] text-[10px] uppercase tracking-[0.14em] text-[var(--faint)]">by Omnisight</div>
                        </div>
                    )}
                </div>
                {NAV.map((item) => navLink(item))}
                <div className="mt-auto">
                    {NAV_END.map((item) => navLink(item))}
                </div>
            </aside>

            <div className="flex min-w-0 flex-1 flex-col">
                {/* Topbar */}
                <header className="flex flex-none items-center gap-2 border-b border-[var(--line)] px-3 py-3 sm:gap-3 sm:px-4 md:px-6 md:py-3.5">
                    {/* Phone: the drawer button and the mark stand in for the rail. */}
                    <button
                        type="button"
                        onClick={() => setNavOpen(true)}
                        aria-label="Open navigation"
                        aria-expanded={navOpen}
                        className={`${iconButton} md:hidden`}
                    >
                        <MenuIcon size={22} />
                    </button>
                    <span className="flex items-center md:hidden">
                        <OmnisightMark size={26} />
                    </span>

                    {/* Location first, then search — you pick the fleet you are looking at, then
                        search within it. All three stand the same height so the row reads as one
                        bar. Organization narrows to a customer, group narrows to a crew, and every
                        page downstream inherits both. Below lg the two pickers fold into the
                        scope sheet behind the funnel button. */}
                    {orgs.length > 1 && (
                        <Combo
                            value={org?.id ?? ""}
                            onChange={setOrgId}
                            size="lg"
                            ariaLabel="Organization"
                            placeholder="Search organizations…"
                            options={orgs.map((o) => ({ value: o.id!, label: o.name ?? "—" }))}
                            action={{ label: "+ Add new organization", onSelect: () => setAddOrgOpen(true) }}
                            className="hidden max-w-[230px] lg:inline-flex"
                        />
                    )}

                    {groups.length > 0 && (
                        <Combo
                            value={group?.id ?? ALL_GROUPS}
                            onChange={setGroupId}
                            size="lg"
                            ariaLabel="Facility group"
                            placeholder="Search groups…"
                            options={[
                                { value: ALL_GROUPS, label: "All groups" },
                                ...groups.map((g) => ({ value: g.id!, label: g.name ?? "—" })),
                            ]}
                            className="hidden max-w-[230px] lg:inline-flex"
                        />
                    )}

                    {/* The search field, where there is room for one. On a phone it is the
                        magnifier button further right, so the field never eats the whole bar. */}
                    <button
                        type="button"
                        onClick={() => setSearchOpen(true)}
                        className="hidden min-h-[46px] w-full max-w-[420px] items-center gap-3 rounded-[13px] border border-[var(--line-2)] bg-[var(--surface)] px-4 text-left text-[14px] text-[var(--faint)] md:flex"
                    >
                        <SearchIcon size={18} />
                        Search anything…
                        <span className="ml-auto text-[12.5px] font-semibold text-[var(--muted)]">⌘ + K</span>
                    </button>

                    {/* Below lg the pickers are folded away, so the bar says in words which fleet
                        the pages are showing — organization over group — and tapping it opens the
                        scope sheet. Truncates rather than pushing the buttons off the phone. */}
                    {hasScope && (
                        <button
                            type="button"
                            onClick={() => setScopeOpen(true)}
                            aria-label="Choose the organization and group"
                            title={scopeLabel}
                            className="min-w-0 max-w-[200px] rounded-[11px] px-2 py-1 text-left hover:bg-[var(--surface-2)] lg:hidden"
                        >
                            {orgs.length > 1 && (
                                <span className="block truncate text-[11.5px] leading-tight text-[var(--muted)]">{org?.name ?? "—"}</span>
                            )}
                            <span className="block truncate text-[13.5px] font-semibold leading-tight">
                                {groups.length > 0 ? (group?.name ?? "All groups") : "No groups"}
                            </span>
                        </button>
                    )}

                    {/* Stretch: everything after this sits against the right edge. */}
                    <span className="flex-1" />

                    <button type="button" onClick={() => setSearchOpen(true)} aria-label="Search" className={`${iconButton} md:hidden`}>
                        <SearchIcon size={20} />
                    </button>

                    {/* The theme button needs the room a phone hasn't got; there it lives in the
                        account menu instead. */}
                    <button
                        type="button"
                        onClick={cycleTheme}
                        aria-label="Switch between day and night"
                        title={`Theme: ${theme}`}
                        className={`${iconButton} hidden md:grid`}
                    >
                        {theme === "light" ? <SunIcon size={20} /> : theme === "dark" ? <MoonIcon size={20} /> : <SunMoonIcon size={20} />}
                    </button>

                    {/* Account: name, org, and the two things you actually do from here. */}
                    <Menu
                        trigger={({ toggle }) => (
                            <button
                                type="button"
                                onClick={toggle}
                                className="flex items-center gap-3 text-left md:border-l md:border-[var(--line)] md:pl-3"
                                aria-haspopup="menu"
                            >
                                <span className="grid h-[46px] w-[46px] flex-none place-items-center rounded-full bg-gradient-to-br from-[var(--accent)] to-[#1B3E8C] text-[15px] font-bold text-white">
                                    {initials}
                                </span>
                                <span className="hidden max-w-[18ch] sm:block">
                                    <span className="block truncate text-[14.5px] font-semibold leading-tight">{user.name ?? user.email}</span>
                                    <span className="block truncate text-[12px] text-[var(--muted)]">{org?.name ?? ""}</span>
                                </span>
                            </button>
                        )}
                    >
                        {({ close }) => (
                            <>
                                <MenuHeader title={user.name ?? user.email ?? ""} sub={user.email} />
                                <MenuItem
                                    icon={<AccountIcon size={16} />}
                                    onClick={() => {
                                        close();
                                        setProfileOpen(true);
                                    }}
                                >
                                    View account
                                </MenuItem>
                                <MenuItem
                                    icon={<PlusIcon size={16} />}
                                    onClick={() => {
                                        close();
                                        setAddOrgOpen(true);
                                    }}
                                >
                                    Add new organization
                                </MenuItem>
                                <MenuItem
                                    icon={theme === "light" ? <SunIcon size={16} /> : theme === "dark" ? <MoonIcon size={16} /> : <SunMoonIcon size={16} />}
                                    onClick={cycleTheme}
                                >
                                    Theme · {THEME_LABEL[theme] ?? theme}
                                </MenuItem>
                                <MenuItem icon={<LogoutIcon size={16} />} danger onClick={logout}>
                                    Log out
                                </MenuItem>
                            </>
                        )}
                    </Menu>
                </header>

                {/* overflow-x-hidden is the frame's guarantee, not a fix: every wide thing (a table
                    on a laptop) scrolls inside its own card, so anything that would scroll the page
                    sideways is a bug — and this keeps it from taking the whole page with it. */}
                <main className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden p-4 md:px-[30px] md:py-[22px]">
                    <Outlet />
                    <footer className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-8 text-[10.5px] leading-relaxed text-[var(--faint)] print:hidden">
                        <span>Omnisight Fleet Commander · © Omnisight, Inc.</span>
                        <Link to="/terms" className="font-semibold text-[var(--muted)] hover:text-[var(--text)]">
                            Terms and Conditions
                        </Link>
                    </footer>
                </main>
            </div>

            {/* Phone navigation drawer: the rail, slid in from the left. */}
            <div
                className={`fixed inset-0 z-40 md:hidden ${navOpen ? "" : "pointer-events-none"}`}
                aria-hidden={!navOpen}
                role="dialog"
                aria-label="Navigation"
            >
                <button
                    type="button"
                    aria-label="Close navigation"
                    tabIndex={navOpen ? 0 : -1}
                    onClick={() => setNavOpen(false)}
                    className={`absolute inset-0 bg-black/60 transition-opacity duration-200 ${navOpen ? "opacity-100" : "opacity-0"}`}
                />
                <div
                    className={`absolute inset-y-0 left-0 flex w-[min(300px,86vw)] flex-col overflow-y-auto border-r border-[var(--line)] bg-[var(--side)] px-3.5 py-4 transition-transform duration-[220ms] ${
                        navOpen ? "translate-x-0" : "-translate-x-full"
                    }`}
                    style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
                >
                    <div className="mb-5 flex items-center gap-2 px-1">
                        <OmnisightMark size={26} />
                        <div className="min-w-0">
                            <div className="text-[15px] font-bold leading-tight tracking-tight">Fleet Commander</div>
                            <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--faint)]">by Omnisight</div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setNavOpen(false)}
                            aria-label="Close navigation"
                            tabIndex={navOpen ? 0 : -1}
                            className="ml-auto grid h-[36px] w-[36px] flex-none place-items-center rounded-lg text-[var(--faint)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
                        >
                            <CloseIcon size={18} />
                        </button>
                    </div>

                    {/* Which fleet the pages are showing, and the way to change it. */}
                    {hasScope && (
                        <button
                            type="button"
                            tabIndex={navOpen ? 0 : -1}
                            onClick={() => {
                                setNavOpen(false);
                                setScopeOpen(true);
                            }}
                            className="mb-4 flex items-center gap-3 rounded-[11px] border border-[var(--line-2)] bg-[var(--surface)] px-3 py-2.5 text-left"
                        >
                            <FilterIcon size={18} className="flex-none text-[var(--muted)]" />
                            <span className="min-w-0 flex-1">
                                <span className="block text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[var(--faint)]">Viewing</span>
                                <span className="block truncate text-[13.5px] font-semibold">{scopeLabel || "—"}</span>
                            </span>
                        </button>
                    )}

                    {NAV.map((item) => navLink(item, false))}
                    <div className="mt-auto pt-4">{NAV_END.map((item) => navLink(item, false))}</div>
                </div>
            </div>

            {/* Organization and group, for widths where the topbar has no room for the pickers.
                Native selects on purpose: a phone's own picker beats a popover on a small screen. */}
            <Sheet open={scopeOpen} onClose={() => setScopeOpen(false)} title="Fleet scope" subtitle="Every page shows this organization and group">
                {orgs.length > 1 && (
                    <label className="flex flex-col gap-1.5 text-[12px] text-[var(--muted)]">
                        Organization
                        <Select value={org?.id ?? ""} onChange={(e) => setOrgId(e.target.value)} className="min-h-11 w-full text-[14px] text-[var(--text)]">
                            {orgs.map((o) => (
                                <option key={o.id} value={o.id}>
                                    {o.name ?? "—"}
                                </option>
                            ))}
                        </Select>
                    </label>
                )}
                {/* The picker's "+ Add new organization" row, for the sheet. */}
                <Btn
                    tone="quiet"
                    onClick={() => {
                        setScopeOpen(false);
                        setAddOrgOpen(true);
                    }}
                    className="w-full"
                >
                    + Add new organization
                </Btn>
                {groups.length === 0 && (
                    <p className="text-[12.5px] leading-relaxed text-[var(--muted)]">
                        {org?.name ?? "This organization"} has no facility groups, so every page shows the whole organization.
                    </p>
                )}
                {groups.length > 0 && (
                    <label className="flex flex-col gap-1.5 text-[12px] text-[var(--muted)]">
                        Group
                        <Select
                            value={group?.id ?? ALL_GROUPS}
                            onChange={(e) => setGroupId(e.target.value)}
                            className="min-h-11 w-full text-[14px] text-[var(--text)]"
                        >
                            <option value={ALL_GROUPS}>All groups</option>
                            {groups.map((g) => (
                                <option key={g.id} value={g.id}>
                                    {g.name ?? "—"}
                                </option>
                            ))}
                        </Select>
                    </label>
                )}
                <div className="h-2" />
            </Sheet>

            <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
            <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
            <AddOrganizationDialog open={addOrgOpen} onClose={() => setAddOrgOpen(false)} />
        </div>
    );
};
